// src/hooks/useKeycloakAuth.tsx

import { useEffect, useState } from "react";
import { useUserStore } from "../stores/userStore";
import { IKeycloak, useKeycloak } from "./Keycloak/useKeycloak";
import { keycloakConfig } from "./KeycloakWrapper";



const MAX_RETRIES = 3;
const RETRY_COUNT_KEY = "loginRetryCount";


export const useKeycloakAuth = () => {
    // 2) Pull in your Zustand setter
    const setUser = useUserStore((s) => s.setUser);

    const [hasCorrectRole, setHasCorrectRole] = useState(false);
    const [loginErrorMessage, setLoginErrorMessage] = useState<string | null>(
        null
    );

    // 3) Inline init options so TS infers them correctly
    const {
        keycloak,
        isAuthenticated,
        isAuthenticating,
        error,
        roles,
        login,
    } = useKeycloak(
        keycloakConfig,
        {
            flow: "standard",
            onLoad: "login-required",
            checkLoginIframe: false,
        },
        "prod",
        "preferred_username",
        60,
        2 * 60 * 1000
    );

    // 4) When we become authenticated, stash the parsed token in your store
    useEffect(() => {
        if (isAuthenticated && keycloak?.tokenParsed) {
            const t = keycloak.tokenParsed;
            setUser({
                username: t.preferred_username,
                roles: t.realm_access?.roles || [],
                givenName: t.given_name,
                familyName: t.family_name,
                sub: t.sub,
                token: keycloak.token,
            });
        }
    }, [isAuthenticated, keycloak, setUser]);

    // 5) Check roles
    useEffect(() => {
        if (roles) {
            const ok = roles.includes("cockpitUser") || roles.includes("admin");
            setHasCorrectRole(ok);
            if (!ok) console.warn("User lacks required role");
        }
    }, [roles]);

    // 6) Retry logic on error
    useEffect(() => {
        if (error) {
            const stored = sessionStorage.getItem(RETRY_COUNT_KEY);
            const count = stored ? parseInt(stored, 10) : 0;

            if (count < MAX_RETRIES) {
                sessionStorage.setItem(RETRY_COUNT_KEY, (count + 1).toString());
                console.warn(`Login error, retry #${count + 1}`);
                login?.();
            } else {
                sessionStorage.setItem(RETRY_COUNT_KEY, "0");
                setLoginErrorMessage(
                    "Die Authentifizierung ist nach mehreren Versuchen fehlgeschlagen. Bitte aktualisieren Sie die Seite oder versuchen Sie es später erneut."
                );
            }
        }
    }, [error, login]);

    return { keycloak, isAuthenticated, isAuthenticating, hasCorrectRole, loginErrorMessage };
};

export default useKeycloakAuth;
